/* eslint-disable camelcase */
import { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import { Box, Typography, Paper, Card, Grid, Button, Divider, LinearProgress, Avatar, Chip } from "@mui/material"
import {
  CloudOff,
  CloudDone,
  Sync,
  AssignmentLate,
  ReportProblem,
  CheckCircle,
  Person,
  Public,
} from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema adaptado a la identidad SIAC
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const SincronizacionDelegado = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isSyncing, setIsSyncing] = useState(false)
  const [progreso, setProgreso] = useState(0)

  // Colas de almacenamiento local
  const [actasEnCola, setActasEnCola] = useState([])
  const [incidenciasEnCola, setIncidenciasEnCola] = useState([])

  // Estados de Seguridad y RLS (Visión Global)
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // ========================================================
  // 1. INICIALIZACIÓN Y MONITOREO
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarContextoYColas()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const cargarContextoYColas = async () => {
    // 1. Cargar colas locales de IndexedDB
    const colaActas = (await localforage.getItem("actas_offline_queue")) || []
    const colaIncidencias = (await localforage.getItem("incidencias_offline_queue")) || []

    setActasEnCola(colaActas)
    setIncidenciasEnCola(colaIncidencias)

    // 2. Cargar contexto del usuario (RLS) para la UI
    try {
      if (!navigator.onLine) throw new Error("Offline")

      // Usamos el endpoint base del delegado para extraer su recinto/rol
      const res = await api.post("/ControlElectoral/getDatosDelegado", {})
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success) {
        setRecintoActual(data.recinto || "")
        setEsGlobal(data.es_global || false)

        await localforage.setItem("sync_recinto_cache", data.recinto || "")
        await localforage.setItem("sync_global_cache", data.es_global || false)
      }
    } catch (err) {
      console.log("Cargando contexto RLS desde caché...")
      const rCache =
        (await localforage.getItem("sync_recinto_cache")) || (await localforage.getItem("actas_recinto_cache")) || ""
      const gCache =
        (await localforage.getItem("sync_global_cache")) || (await localforage.getItem("actas_global_cache")) || false

      setRecintoActual(rCache)
      setEsGlobal(gCache)
    }
  }

  // ========================================================
  // 2. MOTOR DE SINCRONIZACIÓN MASIVA (BULK SYNC)
  // ========================================================
  const handleSincronizarTodo = async () => {
    if (isOffline) {
      return Swal.fire("Sin Conexión", "No puede sincronizar sin señal de internet.", "error")
    }

    const totalItems = actasEnCola.length + incidenciasEnCola.length
    if (totalItems === 0) return

    setIsSyncing(true)
    setProgreso(0)
    let itemsProcesados = 0

    let actasRestantes = [...actasEnCola]
    let incidenciasRestantes = [...incidenciasEnCola]
    let errores = 0

    // A. Subir Actas
    for (let i = 0; i < actasEnCola.length; i++) {
      try {
        const res = await api.post("/recepcion/acta", actasEnCola[i])
        const data = res?.data !== undefined ? res.data : res
        if (data.success) {
          actasRestantes = actasRestantes.filter((a) => a.junta_codigo !== actasEnCola[i].junta_codigo)
        } else {
          errores++
        }
      } catch (err) {
        errores++
        console.error("Fallo subiendo acta", err)
      }
      itemsProcesados++
      setProgreso((itemsProcesados / totalItems) * 100)
    }

    // B. Subir Incidencias
    for (let i = 0; i < incidenciasEnCola.length; i++) {
      try {
        const res = await api.post("/ControlElectoral/recepcionIncidencia", incidenciasEnCola[i])
        const data = res?.data !== undefined ? res.data : res
        if (data.success) {
          // Corrección de condición constante aquí
          incidenciasRestantes = incidenciasRestantes.filter((_, index) => index !== i)
        } else {
          errores++
        }
      } catch (err) {
        errores++
        console.error("Fallo subiendo incidencia", err)
      }
      itemsProcesados++
      setProgreso((itemsProcesados / totalItems) * 100)
    }

    // C. Actualizar Memoria del Celular
    await localforage.setItem("actas_offline_queue", actasRestantes)
    await localforage.setItem("incidencias_offline_queue", incidenciasRestantes)
    setActasEnCola(actasRestantes)
    setIncidenciasEnCola(incidenciasRestantes)
    setIsSyncing(false)

    // D. Feedback al Usuario
    if (errores === 0) {
      Swal.fire("¡Sincronización Exitosa!", "Todos los datos han sido transmitidos al Centro de Cómputo.", "success")
    } else {
      Swal.fire(
        "Sincronización Incompleta",
        `Se subió la mayoría, pero ${errores} paquete(s) fallaron por micro-cortes de red. Reintente en un momento.`,
        "warning",
      )
    }
  }

  const totalPendientes = actasEnCola.length + incidenciasEnCola.length

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        {/* PERFIL DINÁMICO (DELEGADO VS ADMIN GLOBAL) */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            background: esGlobal
              ? "linear-gradient(135deg, #0d3b4a 0%, #051c24 100%)"
              : "linear-gradient(135deg, #196C87 0%, #114b5e 100%)",
            color: "white",
          }}
        >
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", width: 60, height: 60, mr: 2 }}>
            {esGlobal ? <Public fontSize="large" /> : <Person fontSize="large" />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {esGlobal ? "Administrador Global" : "Delegado Activo"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {esGlobal ? "Centro de Cómputo" : "Módulo de Transmisión de Datos"}
            </Typography>
          </Box>
        </Paper>

        {/* INDICADOR DE COBERTURA (RLS) */}
        {recintoActual && (
          <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
            <Typography variant="subtitle2" color="primary.dark" fontWeight="bold" display="flex" alignItems="center">
              📍 Cobertura: {recintoActual}
            </Typography>
            {esGlobal && (
              <Chip
                icon={<Public fontSize="small" />}
                label="MODO GLOBAL"
                color="info"
                size="small"
                sx={{ ml: 2, fontWeight: "bold" }}
              />
            )}
          </Box>
        )}

        {/* ESTADO DE CONEXIÓN */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "white",
          }}
        >
          {isOffline ? (
            <Box display="flex" alignItems="center" color="error.main">
              <CloudOff sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                SIN CONEXIÓN A RED
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" color="success.main">
              <CloudDone sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                CONEXIÓN ESTABLE
              </Typography>
            </Box>
          )}
        </Paper>

        {/* INDICADORES DE COLA (IndexedDB) */}
        <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
          Resumen de Tareas Pendientes
        </Typography>

        <Grid container spacing={2} mb={4}>
          <Grid item xs={6}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                borderTop: actasEnCola.length > 0 ? "4px solid #ed6c02" : "4px solid #2E7D32",
                textAlign: "center",
                py: 2,
              }}
            >
              <AssignmentLate sx={{ fontSize: 40, color: actasEnCola.length > 0 ? "#ed6c02" : "#2E7D32", mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color="textPrimary">
                {actasEnCola.length}
              </Typography>
              <Typography variant="caption" color="textSecondary" fontWeight="bold">
                ACTAS EN COLA
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={6}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                borderTop: incidenciasEnCola.length > 0 ? "4px solid #d32f2f" : "4px solid #2E7D32",
                textAlign: "center",
                py: 2,
              }}
            >
              <ReportProblem
                sx={{ fontSize: 40, color: incidenciasEnCola.length > 0 ? "#d32f2f" : "#2E7D32", mb: 1 }}
              />
              <Typography variant="h3" fontWeight="bold" color="textPrimary">
                {incidenciasEnCola.length}
              </Typography>
              <Typography variant="caption" color="textSecondary" fontWeight="bold">
                ALERTAS EN COLA
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* BARRA DE PROGRESO DE SINCRONIZACIÓN */}
        {isSyncing && (
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold" color="primary">
                Transmitiendo paquetes...
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {Math.round(progreso)}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progreso} sx={{ height: 10, borderRadius: 5 }} />
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* BOTÓN MAESTRO DE SINCRONIZACIÓN */}
        {totalPendientes === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 4, textAlign: "center", bgcolor: "#e8f5e9", borderRadius: 3, border: "1px dashed #2E7D32" }}
          >
            <CheckCircle sx={{ fontSize: 48, color: "#2E7D32", mb: 1 }} />
            <Typography variant="h6" color="success.main" fontWeight="bold">
              TODO AL DÍA
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No hay datos pendientes en su dispositivo.
            </Typography>
          </Paper>
        ) : (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            startIcon={<Sync />}
            onClick={handleSincronizarTodo}
            disabled={isOffline || isSyncing}
            sx={{ py: 2, fontSize: "1.2rem", borderRadius: 2, fontWeight: "bold", boxShadow: 4 }}
          >
            {isSyncing ? "ENVIANDO DATOS..." : "SINCRONIZAR TODO AHORA"}
          </Button>
        )}
      </div>
    </ThemeProvider>
  )
}

export default SincronizacionDelegado
